import algoliasearch, { SearchIndex } from 'algoliasearch';

import { createInMemoryCache } from '@algolia/cache-in-memory';
import { Caterer, Listing } from 'lib/algolia/model';

const algoliaAppId = process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID;
const algoliaSearchApiKey = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY;

type SearchOptions = Parameters<SearchIndex['search']>[1];

if (!algoliaAppId) {
  throw new Error('Missing ENV Variable: Algolia app id');
}
if (!algoliaSearchApiKey) {
  throw new Error('Missing ENV Variable: Algolia search api key');
}

const client = algoliasearch(algoliaAppId, algoliaSearchApiKey, {
  responsesCache: createInMemoryCache(),
  requestsCache: createInMemoryCache({ serializable: false }),
});

/*
 * INDEXES
 */
const INDEX_PREFIX = 'production';

const catererMarketplaceIndex = createIndex('caterer_marketplace');
const listingsMarketplaceIndex = createIndex('listings_marketplace');
const numberOfOrdersIndex = createIndex(
  'caterer_marketplace_number_of_orders_desc'
);
const ratingScoreIndex = createIndex('caterer_marketplace_rating_score_desc');
const catererCreatedAtSortedIndex = createIndex(
  'caterer_marketplace_created_at_desc'
);
const listingsCreatedAtSortedIndex = createIndex(
  'listings_marketplace_created_at_desc'
);

export const getCaterer = (id: string): Promise<Caterer | undefined> => {
  return catererMarketplaceIndex.getObject<Caterer>(id);
};

/*
 * SEARCH FUNCTIONS
 */
export const searchRecentCaterers = createSearchHandler(
  catererCreatedAtSortedIndex
);

export const searchRecentListings = createSearchHandler(
  listingsCreatedAtSortedIndex
);

export const searchCaterers = createSearchHandler<Caterer>(
  catererMarketplaceIndex
);
export const searchListings = createSearchHandler<Listing>(
  listingsMarketplaceIndex
);
export const searchMostBooked = createSearchHandler<Caterer>(
  numberOfOrdersIndex
);
export const searchBestRated = createSearchHandler<Caterer>(ratingScoreIndex);

export interface CatererSearchOptions extends NonNullable<SearchOptions> {
  city?: string;
  keywords?: string[];
  method: 'topRated' | 'mostBooked';
}

export const searchCaterersBy = ({
  city,
  keywords,
  method,
  ...searchOptions
}: CatererSearchOptions) => {
  const fetcher = method === 'mostBooked' ? searchMostBooked : searchBestRated;

  const cityFilter = city ? { filters: `cities:"${city}"` } : {};

  return fetcher(keywords, {
    ...cityFilter,
    ...searchOptions,
  });
};

/*
 * UTILS
 */
function createSearchHandler<T>(index: SearchIndex) {
  return async (
    queryString: string | string[] = '',
    params?: SearchOptions
  ) => {
    const query = Array.isArray(queryString)
      ? queryString.join(' ')
      : queryString;

    const { hits } = await index.search<T>(query, params);

    return hits;
  };
}

function createIndex(name: string) {
  return client.initIndex(`${INDEX_PREFIX}_${name}`);
}
